import { type Account, AccountStatus, AccountType, ResourceUserRole } from '../dto/account';
import type { Rights } from '../state/account/types';
import { UserType } from '../state/account/types';
import { isContractorPersona, isEnterprisePersona } from '../utils/persona';

/**
 * Who the customer is, as one word.
 *
 * A persona is not a permission. `useRights` remains the authority for tier and
 * scope - who may do what, on which company, office, client and project - and
 * this says what that tier is looking at. The two compose: the registry adds a
 * dimension rights never had, and overrides only where a persona genuinely
 * changes the answer.
 *
 * It exists because the alternative was already happening. The same persona
 * test was written out by hand in a dozen branches across the rail, the
 * dashboard router, the AI Hub and the estimate summary, and they had begun to
 * disagree with each other. A difference between personas should be a line in
 * one table, not a condition discovered in whichever file someone opens next.
 */
export type PersonaKey = 'contractor' | 'enterprise' | 'provider' | 'client' | 'expert';

/** The dashboard a persona lands on. Ids, not components: this module is in services. */
export type DashboardId = 'enterprise' | 'contractor' | 'home' | 'project-manager' | 'projects';

/** One entry in the left rail. Membership and order live here; the rights gate stays at the call site. */
export type RailItemId =
  | 'dashboard'
  | 'accounts'
  | 'offices'
  | 'projects'
  | 'clients'
  | 'providers'
  | 'network'
  | 'settings'
  | 'transactions'
  | 'new-project'
  | 'ai-hub'
  | 'ai-admin';

/**
 * What kind of number this persona produces, and what to call it.
 *
 * An Opinion of Probable Cost is a consultant's view of what someone else's
 * work should cost. A contractor's estimate is their own bid. They are
 * different documents with different standards behind them, and calling both
 * of them the same thing on screen was the visible half of that conflation.
 */
export interface EstimateDeliverableStandard {
  key: 'opc' | 'contractor_bid' | 'conceptual_range';
  /** i18n key for the Estimate Summary heading. */
  summaryHeadingKey: string;
  summaryHeadingFallback: string;
}

/**
 * Persona answers that differ from the tier's own.
 *
 * Deliberately tiny, and deliberately only ever widening: an override says
 * "this tier, for this persona, may also do this". Narrowing belongs in the
 * rights model, where the audit is.
 */
export interface PersonaCapabilityOverrides {
  /**
   * Tiers that may start a project even when the base rights answer is no.
   *
   * A contractor's Production Manager is an Administrator who runs the work,
   * and work starts with a project. On every other account type an
   * Administrator deliberately cannot, and that stays exactly as it is.
   */
  canCreateProjectTiers?: ResourceUserRole[];
}

export interface PersonaDefinition {
  dashboard: DashboardId;
  /**
   * Which entries this persona's rail carries, in order. There is no treatment
   * to choose: every persona gets the same slim icon rail with tooltips. An
   * enterprise is the same product with a wider portfolio, not a different one
   * with its own chrome, and a second rail treatment only made it look otherwise.
   */
  rail: RailItemId[];
  /** AI Hub tool keys, matching `aiHubTools[].key`. */
  aiHubTools: string[];
  estimateDeliverableStandard: EstimateDeliverableStandard;
  capabilityOverrides?: PersonaCapabilityOverrides;
}

const OPC: EstimateDeliverableStandard = {
  key: 'opc',
  summaryHeadingKey: 'EditProject.EstSummary.OPCLabel',
  summaryHeadingFallback: 'Opinion of Probable Cost Summary',
};

const CONTRACTOR_BID: EstimateDeliverableStandard = {
  key: 'contractor_bid',
  summaryHeadingKey: 'EditProject.EstimateSummary',
  summaryHeadingFallback: 'Estimate Summary',
};

/**
 * The rail everyone who is not an enterprise sees, in the order it renders.
 *
 * Settings sits mid-list rather than at the end because that is where it has
 * always been; the order is recorded here, not redesigned here.
 */
const STANDARD_RAIL: RailItemId[] = [
  'dashboard',
  'accounts',
  'offices',
  'projects',
  'settings',
  'transactions',
  'new-project',
  'ai-hub',
  'ai-admin',
];

/** The estimating tools every persona can reach. */
const COMMON_AI_TOOLS = ['chat', 'concept-check', 'bid-analysis', 'root-cause-tracker'];

export const PERSONA_REGISTRY: Record<PersonaKey, PersonaDefinition> = {
  contractor: {
    dashboard: 'contractor',
    // Network sits beside Projects: the firms you work with belong next to the
    // work, not filed under company settings. Enterprise gets the same screen
    // when its groups are settled.
    rail: [...STANDARD_RAIL.slice(0, 4), 'network', ...STANDARD_RAIL.slice(4)],
    // Both estimate reviews. `qa-qc` is their own pre-turn-in check;
    // `estimate-review` is the same 3-tier review a client runs, which a
    // contractor wants for the estimates they are handed as well as the ones
    // they write. It was left off this list when the contractor persona was
    // built, so the tool had a card, a nav entry and a route that no
    // contractor could see.
    aiHubTools: [...COMMON_AI_TOOLS, 'qa-qc', 'estimate-review'],
    estimateDeliverableStandard: CONTRACTOR_BID,
    capabilityOverrides: { canCreateProjectTiers: [ResourceUserRole.Administrator] },
  },
  enterprise: {
    dashboard: 'enterprise',
    rail: ['dashboard', 'offices', 'projects', 'providers', 'new-project', 'ai-hub', 'settings'],
    aiHubTools: [...COMMON_AI_TOOLS, 'estimate-review'],
    estimateDeliverableStandard: OPC,
  },
  client: {
    dashboard: 'home',
    // Clients see their providers, in the same slot providers see their clients.
    //
    // This was the one half of that pair nobody wrote. A client engages estimating firms and had
    // no rail entry to reach them, so the Providers page existed, had a route, and was reachable
    // only by typing the URL. The entry itself was already defined in MainNav - the persona simply
    // never asked for it.
    rail: [...STANDARD_RAIL.slice(0, 4), 'providers', ...STANDARD_RAIL.slice(4)],
    aiHubTools: [...COMMON_AI_TOOLS, 'estimate-review'],
    estimateDeliverableStandard: OPC,
  },
  provider: {
    // Providers see their clients; nobody else has any.
    rail: [...STANDARD_RAIL.slice(0, 4), 'clients', ...STANDARD_RAIL.slice(4)],
    dashboard: 'home',
    aiHubTools: [...COMMON_AI_TOOLS, 'trade-qaqc', 'full-estimate-qaqc', 'trade-contractor-estimate-review'],
    estimateDeliverableStandard: OPC,
  },
  expert: {
    dashboard: 'projects',
    rail: STANDARD_RAIL,
    // An expert is a resource on someone else's account and does one job here.
    aiHubTools: ['qa-qc'],
    estimateDeliverableStandard: OPC,
  },
};

/**
 * Which persona is looking.
 *
 * The two persona helpers are the lookup key and nothing else now: everything
 * that used to branch on them reads a declaration instead.
 */
export const resolvePersona = (rights?: Rights, account?: Account): PersonaKey => {
  if (isContractorPersona(rights, account)) return 'contractor';
  if (isEnterprisePersona(rights, account)) return 'enterprise';
  if (rights?.userType === UserType.expert) return 'expert';
  if (rights?.accountType === AccountType.Provider) return 'provider';
  if (rights?.accountType === AccountType.Client) return 'client';

  /*
   * An unclaimed shell, read from the company rather than from rights.
   *
   * Rights come from a roster read, and the one person who reaches an invited firm's account is
   * refused it - so an invited estimating firm arrived here with nothing to go on and fell to the
   * last resort below, which is `expert`. They were shown an expert's rail on their own firm.
   *
   * Deliberately narrow. It asks for a Deferred account AND no rights, so it cannot fire for
   * anybody whose rights merely have not loaded yet: their company is Ready or Vetted, not an
   * unclaimed shell, and they keep falling through to the same answer they get today. Whenever
   * rights exist this is unreachable.
   */
  if (!rights && account?.status === AccountStatus.Deferred) {
    if (account.type === AccountType.Provider) return 'provider';
    if (account.type === AccountType.Client) return 'client';
  }

  return 'expert';
};

/**
 * The rail entries a project-scoped guest may actually open.
 *
 * Not a list of what is locked - a statement of what the grant is. Somebody invited to a firm's
 * account is there to reach the work a client shared with them, and their workspace is where that
 * work is. Everything else on the rail belongs to the company whose account it is, and stays
 * visible and locked so they can see what claiming it would give them.
 *
 * The projects list is not a second answer to the same question. It is the company's own list of
 * work - scoped by the server to what a guest may see, so nothing leaks, but still a different
 * surface from the workspace that already lists exactly those projects. One door.
 */
export const GUEST_REACHABLE_RAIL: RailItemId[] = ['dashboard'];

/**
 * The entries an invited firm is shown before they claim the account.
 *
 * Curated, and deliberately not the persona's whole rail. This rail answers "what is this account
 * for, and what would claiming it give me", so it carries the things that become theirs: the work
 * a client shared with them, the firms on the other side of that work, and their own company
 * settings. It does not carry the platform's own administration.
 *
 * What is left off, and why:
 * - `accounts` is the cross-account admin list and `ai-admin` is Construction Check's own prompt
 *   management. Neither becomes theirs on claiming, so showing them padlocked would advertise
 *   screens this customer has in no state. They are also the two further gear icons on a rail
 *   that already has a gear.
 * - `offices` is multi-office administration, which is not what an invited firm needs first, and
 *   its building read as a repeat of Clients two slots up.
 * - `transactions` is billing history an unclaimed shell has none of.
 * - `new-project` is an action rather than a destination: a padlocked create button invites a
 *   click that cannot land, and on this persona it is the client who starts the project.
 * - `ai-hub` arrives with the tier, not with the claim.
 */
const INVITED_FIRM_RAIL: RailItemId[] = ['dashboard', 'projects', 'clients', 'providers', 'settings'];

/**
 * The invited-firm rail, as a subset of the persona's own.
 *
 * Derived by filtering the persona's rail rather than by listing ids, which is what makes it a
 * subset by construction: it cannot gain an entry the persona does not carry, cannot repeat one,
 * and cannot reorder them. The counterparty slot then resolves itself - a provider's rail carries
 * `clients` and a client's carries `providers`, and no persona carries both.
 *
 * Before this, a guest was shown `persona.rail` whole. That was written when the guest answer was
 * always false, so it never ran; the moment a project-scoped guest was correctly recognised it
 * put ten entries on an invited firm's rail, including two the platform keeps for itself.
 */
export const invitedFirmRail = (persona: PersonaDefinition): RailItemId[] =>
  persona.rail.filter((id) => INVITED_FIRM_RAIL.includes(id));

export const personaDefinition = (rights?: Rights, account?: Account): PersonaDefinition =>
  PERSONA_REGISTRY[resolvePersona(rights, account)];
